import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { formatDate } from '@angular/common';
import { finalize } from 'rxjs/operators';
import {
  productTypeLabel,
  Subscription,
  SubscriptionProductType,
  SubscriptionRenewalRule,
  SubscriptionStatus,
  SubscriptionUpdateRequest,
} from '../../model/v3/subscriptions';
import { ManagerUtils } from '../../services/utils/v3/manager-utils';
import { SubscriptionsV3RestService } from '../../services/rest/v3/subscriptions-rest.service';
import { ModalComponent } from '../modal/modal.component';

type RenewalRuleChoice = 'none' | 'months' | 'until';

/**
 * Renewal-rule editor for one subscription; opened from the subscription list
 * and from the manager's student page. Saves through PATCH and emits the
 * updated subscription.
 */
@Component({
  selector: 'app-subscription-rule-modal',
  templateUrl: './subscription-rule-modal.component.html',
  standalone: false,
})
export class SubscriptionRuleModalComponent {
  @Input() schoolId: number;
  @Output() saved = new EventEmitter<Subscription>();

  @ViewChild('modal', { static: true })
  modal: ModalComponent;

  subscription: Subscription | null = null;
  autoRenew = true;
  rule: RenewalRuleChoice = 'none';
  months: number | null = null;
  untilDate: string | null = null;
  notes = '';
  saving = false;
  error: string | null = null;

  readonly SubscriptionStatus = SubscriptionStatus;

  constructor(private subscriptionsRest: SubscriptionsV3RestService) {}

  open(subscription: Subscription) {
    this.subscription = subscription;
    this.autoRenew = subscription.renewal.autoRenew;
    this.rule = subscription.renewal.renewalMonthsLimit != null
      ? 'months'
      : subscription.renewal.renewUntilDate
        ? 'until'
        : 'none';
    this.months = subscription.renewal.renewalMonthsLimit ?? null;
    this.untilDate = subscription.renewal.renewUntilDate?.substring(0, 10) ?? null;
    this.notes = '';
    this.error = null;
    this.modal.show();
  }

  personName(subscription: Subscription): string {
    return ManagerUtils.getName(subscription.person);
  }

  productLabel(subscription: Subscription): string {
    return productTypeLabel(subscription.productType);
  }

  /**
   * The books subscription deserves an extra warning: the real product access
   * is not integrated yet, so an expiry stops the billing while the teacher
   * keeps the books, and only a product re-grant reopens it.
   */
  isBooksSubscription(subscription: Subscription): boolean {
    return subscription.productType === SubscriptionProductType.TeacherSubscription;
  }

  get untilDateInPast(): boolean {
    if (this.rule !== 'until' || !this.untilDate) {
      return false;
    }
    const today = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    return this.untilDate < today;
  }

  get ruleEditValid(): boolean {
    if (this.rule === 'months') {
      return Number.isInteger(this.months) && this.months >= 1;
    }
    if (this.rule === 'until') {
      return !!this.untilDate;
    }
    return true;
  }

  save() {
    const subscription = this.subscription;
    if (!subscription || !this.ruleEditValid) {
      return;
    }
    // the rule is replaced as a whole: limits omitted here are cleared
    const renewal: SubscriptionRenewalRule = { autoRenew: this.autoRenew };
    if (this.rule === 'months') {
      renewal.renewalMonthsLimit = this.months;
    } else if (this.rule === 'until') {
      renewal.renewUntilDate = this.untilDate;
    }
    const request: SubscriptionUpdateRequest = { renewal };
    if (this.notes.trim()) {
      request.notes = this.notes.trim();
    }
    this.saving = true;
    this.error = null;
    this.subscriptionsRest
      .updateSubscription(this.schoolId, subscription.id, request)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (updated) => {
          this.modal.hide();
          this.saved.emit(updated);
        },
        error: (error) => {
          this.error = error?.error?.message ?? 'Saving the renewal rule failed';
        },
      });
  }
}
